import React, { useMemo, useState, useEffect } from 'react';
import Navbar from './navbar';
import 'react-reflex/styles.css';
import { ReflexContainer, ReflexElement } from 'react-reflex';
import ChatAndDocumentContainer from './chatAndDocumentContainer';
import { useNavigate, useParams } from 'react-router';
import { NoInteractionView, AlertType, alert } from '@quyva/shared-ui';
import axios from 'axios';
import { getUrl } from '../utils';
import { useSearchParams } from 'react-router-dom';
import {
    DataType,
    RouterPath,
    defaultLanguage,
    supportedLanguages,
} from '@quyva/common';
import { Config } from '../config/envConfig';

const generateSessionId = () => {
    let sessionId = sessionStorage.getItem('sessionId');
    if (!sessionId) {
        sessionId = crypto.randomUUID(); // Generate unique session ID
        sessionStorage.setItem('sessionId', sessionId);
    }
};

const fetchProductCategoriesAndProducts = async (
    user,
    setProductCategoriesAndProductsList
) => {
    try {
        if (user) {
            const token = user.token;
            const dynamicUrl = getUrl(
                `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/customer_chat_routes/get-non-vendor-product-categories-and-products?only_mapped=true`
            );

            const response = await axios.get(dynamicUrl, {
                headers: {
                    Authorization: token,
                },
            });
            // Generate and store session ID after the API call

            generateSessionId();

            const data = response.data.userProductCategoriesAndProducts;
            if (data) {
                setProductCategoriesAndProductsList(data);
            } else {
                setProductCategoriesAndProductsList([]);
            }
        }
    } catch (error) {
        alert(
            AlertType.ERROR,
            'Error fetching product categories and products'
        );
    }
};
// Reverse mapping'
const languageMap = Object.fromEntries(
    Object.entries(supportedLanguages).map(([key, value]) => [value, key])
);
const MainComponent = ({
    selectedProductCategory,
    setSelectedProductCategory,
    selectedProduct,
    setSelectedProduct,
    productCategoriesAndProductsList,
    setProductCategoriesAndProductsList,
    vendorDetails,
    fetchVendorDetails,
    verifyAuthentication,
    isSmallScreen,
    setAuthState,
    enableAudioFeature,
    setEnableAudioFeature,
    hasProductCategories,
    hasProducts,
    socket,
}) => {
    const [searchParams] = useSearchParams();
    const hasAudioParams = searchParams.get('audio') === 'true'; // Check if audio parameter is true
    const [isLoading, setIsLoading] = useState(false);

    const [repoHasDocMappings, setRepoHasDocMappings] = useState(true);

    const supportRepoId = vendorDetails?.repoId;
    const isGlobalRepo = !!vendorDetails?.isGlobalRepo;

    useEffect(() => {
        (async () => {
            try {
                // If no repoId in ctx, keep existing behavior.
                if (!supportRepoId) {
                    if (!repoHasDocMappings) {
                        setRepoHasDocMappings(true);
                    }
                    return;
                }

                // For ALL/global repo links we still want product selectors.
                if (isGlobalRepo) {
                    if (!repoHasDocMappings) {
                        setRepoHasDocMappings(true);
                    }
                    return;
                }

                const dynamicUrl = getUrl(
                    `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/customer_chat_routes/get-file-product-mappings/${supportRepoId}`
                );

                const resp = await axios.get(dynamicUrl);
                const mappings = resp?.data?.repoFileProductMappings;
                const hasMappings = Array.isArray(mappings) && mappings.length > 0;
                setRepoHasDocMappings(hasMappings);

                // Repo-scoped links must always be repo-level chat (no category/product UI).
                if (!isGlobalRepo) {
                    setProductCategoriesAndProductsList([]);
                    setSelectedProductCategory(null);
                    setSelectedProduct(null);
                }
            } catch (e) {
                // Fail open: if we can't check, keep existing product selector behavior.
                setRepoHasDocMappings(true);
            }
        })();
    }, [supportRepoId, isGlobalRepo]);

    const showProductSelectors = useMemo(() => {
        // Repo-scoped links must always be repo-level chat (no category/product UI),
        // except for ALL/global repo links.
        if (supportRepoId && !isGlobalRepo) return false;
        return true;
    }, [repoHasDocMappings, supportRepoId, isGlobalRepo]);

    const effectiveHasProductCategories = useMemo(() => {
        if (!showProductSelectors) return false;
        return hasProductCategories;
    }, [hasProductCategories, showProductSelectors]);

    const effectiveHasProducts = useMemo(() => {
        if (!showProductSelectors) return false;
        return hasProducts;
    }, [hasProducts, showProductSelectors]);

    //responsive mode functions and variables
    const [showPdf, setShowPdf] = useState(false);
    const [onButton, setOnButton] = useState(false);

    // Language, voice and music related settings
    const [language, setLanguage] = useState(
        languageMap[vendorDetails?.language] ?? defaultLanguage
    );

    const [enableReader, setEnableReader] = useState(true);
    const [enableMusic, setEnableMusic] = useState(true);

    const [enableSpeaker, setEnableSpeaker] = useState(false);
    const [activeAudioId, setActiveAudioId] = useState(null);
    const [audioUrlData, setAudioUrlData] = useState({});

    const { vendorId } = useParams(); // Extract the vendorId from the URL path
    const navigate = useNavigate();

    const user = JSON.parse(localStorage.getItem('productSupport'))[vendorId];

    useEffect(() => {
        if (hasAudioParams && !enableAudioFeature) {
            setEnableAudioFeature(true);
        }
    }, []);

    useEffect(() => {
        // For repo-scoped links we derive the category/product list from mappings.
        // For ALL/global repo links, keep the user product categories/products selector.
        if (supportRepoId && !isGlobalRepo) return;

        if (user.token) {
            setIsLoading(true);
            (async () => {
                await fetchProductCategoriesAndProducts(
                    user,
                    setProductCategoriesAndProductsList
                );
            })();
            setIsLoading(false);
        }
    }, [supportRepoId, isGlobalRepo]);

    useEffect(() => {
        if (!vendorId) {
            navigate(`/${RouterPath.NOT_FOUND}`);
            return;
        }
        if (!vendorDetails) {
            (async () => {
                const isFetched = await fetchVendorDetails(vendorId);
                if (!isFetched) {
                    navigate(`/${RouterPath.NOT_FOUND}`);
                }
            })();
        }
    }, [vendorDetails, vendorId]);

    useEffect(() => {
        const handleBeforeUnload = (event) => {
            localStorage.setItem('isRefresh', 'true'); // Flag to indicate that the page was loaded

            // Retrieve productSupport and temp data from localStorage
            // let tempData = JSON.parse(localStorage.getItem('tempData'));
            let productSupport = JSON.parse(
                localStorage.getItem('productSupport')
            );
            // const vendorData = productSupport[vendorId];

            // Check if productSupport exists and vendorId exists in the object
            // if (!tempData) {
            //     tempData = {};
            // }

            // tempData[vendorId] = vendorData;
            // localStorage.setItem('tempData', JSON.stringify(tempData));

            delete productSupport[vendorId];
            // Update the localStorage with the modified productSupport
            localStorage.setItem(
                'productSupport',
                JSON.stringify(productSupport)
            );
        };

        // const handleLoad = () => {
        //     // Check if the 'isRefresh' flag exists in localStorage
        //     const isRefresh = localStorage.getItem('isRefresh');

        //     // If the flag is present, it indicates a refresh; otherwise, it's a tab close
        //     if (isRefresh) {
        //         // For refresh, just clear the flag (so it can persist on refresh)
        //         localStorage.removeItem('isRefresh');

        //         // Retrieve productSupport data from localStorage
        //         let tempData = JSON.parse(localStorage.getItem('tempData'));
        //         // Retrieve productSupport data from localStorage
        //         let productSupport = JSON.parse(
        //             localStorage.getItem('productSupport')
        //         );
        //         const vendorData = tempData[vendorId];

        //         // Check if productSupport exists and vendorId exists in the object
        //         if (vendorData) {
        //             if (!productSupport) {
        //                 productSupport = {};
        //             }
        //             productSupport[vendorId] = vendorData;
        //             // Update the localStorage with the modified productSupport
        //             localStorage.setItem(
        //                 'productSupport',
        //                 JSON.stringify(productSupport)
        //             );

        //             delete tempData[vendorId];
        //             // Update the localStorage with the modified tempData
        //             localStorage.setItem(
        //                 'tempData',
        //                 JSON.stringify(tempData)
        //             );
        //         }
        //     }
        // };

        // Set a flag when the page is loaded
        // window.addEventListener('load', handleLoad);
        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            // Cleanup event listeners on component unmount
            window.removeEventListener('beforeunload', handleBeforeUnload);
            // window.removeEventListener('load', handleLoad);
        };
    }, [vendorId]); // Re-run effect if vendorId changes

    useEffect(() => {
        // Reset both states to false when the component mounts or updates
        setShowPdf(false);
        setOnButton(false);
    }, [isSmallScreen]);

    const togglePdfVisibility = (reset) => {
        if (typeof reset === DataType.STRING) {
            setShowPdf(false);
            setOnButton(false);
        } else {
            setShowPdf((prevState) => !prevState);
            setOnButton((prevState) => !prevState);
        }
    };

    if (isLoading) {
        return <NoInteractionView viewText={'Loading Products Details'} />;
    }

    return (
        <div className='z-0 w-full h-full m-auto my-2'>
            <ReflexContainer orientation='horizontal'>
                <ReflexElement
                    className='header'
                    style={{
                        minHeight: '4rem',
                        height: '4rem',
                        maxHeight: '4rem',
                    }}
                >
                    <div className='pane-content w-full h-full px-2'>
                        <Navbar
                            vendorDetails={vendorDetails}
                            productDetailsList={
                                productCategoriesAndProductsList
                            }
                            selectedProductCategory={selectedProductCategory}
                            selectedProduct={selectedProduct}
                            setSelectedProductCategory={
                                setSelectedProductCategory
                            }
                            setSelectedProduct={setSelectedProduct}
                            isLoading={isLoading}
                            isSmallScreen={isSmallScreen}
                            showProductSelectors={showProductSelectors}
                        />
                    </div>
                </ReflexElement>

                <ReflexElement flex={1}>
                    <div className='pane-content h-full w-full px-2'>
                        <ChatAndDocumentContainer
                            user={user}
                            socket={socket}
                            showPdf={showPdf}
                            onButton={onButton}
                            setShowPdf={setShowPdf}
                            setOnButton={setOnButton}
                            togglePdfVisibility={togglePdfVisibility}
                            selectedProductCategory={selectedProductCategory}
                            selectedProduct={selectedProduct}
                            isSmallScreen={isSmallScreen}
                            verifyAuthentication={verifyAuthentication}
                            setAuthState={setAuthState}
                            enableAudioFeature={enableAudioFeature}
                            hasProductCategories={effectiveHasProductCategories}
                            hasProducts={effectiveHasProducts}
                            enableReader={enableReader}
                            setEnableReader={setEnableReader}
                            enableMusic={enableMusic}
                            setEnableMusic={setEnableMusic}
                            enableSpeaker={enableSpeaker}
                            setEnableSpeaker={setEnableSpeaker}
                            activeAudioId={activeAudioId}
                            setActiveAudioId={setActiveAudioId}
                            audioUrlData={audioUrlData}
                            setAudioUrlData={setAudioUrlData}
                            vendorDetails={vendorDetails}
                        />
                    </div>
                </ReflexElement>
            </ReflexContainer>
        </div>
    );
};

export default MainComponent;
